import FeatureGates, { type GetExperimentValueOptions } from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import type { BackendExperiment } from '@atlassian/search-experiment';

import type { ThirdPartyConfigsBootstrap } from '../../constants/schemas/3p-config';

/**
 * Transforms the requested backend experiments by replacing the 'definitions' field
 */
export function getRequestedBackendExperiment(
	requestedBackendExperiments: BackendExperiment | undefined,
): BackendExperiment | undefined {
	const experimentLayers = requestedBackendExperiments?.experimentLayers?.map((layer) => {
		const { analyticsDefinitions, ...rest } = layer;
		return {
			...rest,
			definitions: analyticsDefinitions,
		};
	});

	return {
		experimentLayers,
	};
}

/**
 * Serves the federated Slack search experiment.
 *
 * @param selectedProduct - The currently selected product (string, or undefined for all products)
 * @param selectedProducts - Array of selected products (takes precedence over selectedProduct if provided)
 * @param options - Options for getting the experiment value
 * @param thirdPartyConfigs - Optional third party configs for auth check
 * @returns boolean - Whether the experiment is enabled
 */
export const serveFederatedSlackExperiment = (
	selectedProduct?: string,
	selectedProducts?: string[],
	options: GetExperimentValueOptions<string> = {
		fireExperimentExposure: false,
	},
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
): boolean => {
	// Determine effective selected product:
	// - If selectedProducts array is provided and non-empty, use it to determine the product
	// - Otherwise fall back to selectedProduct
	const effectiveSelectedProduct =
		selectedProducts && selectedProducts.length > 0
			? selectedProducts.includes('slack')
				? 'slack'
				: selectedProducts[0]
			: selectedProduct;

	// Only serve the experiment when user is under all product search or Slack product search
	const isSlackOrAllProducts = !effectiveSelectedProduct || effectiveSelectedProduct === 'slack';
	if (!isSlackOrAllProducts) {
		return false;
	}

	// Return false if Slack is not authorized (skip check if thirdPartyConfigs not provided)
	if (thirdPartyConfigs) {
		const isSlackAuthorized = thirdPartyConfigs.slack?.userNeedsOAuth === false;
		if (!isSlackAuthorized) {
			return false;
		}
	}

	const experimentValue = FeatureGates.getExperimentValue<string>(
		'ai-3p-slack-federated-search-analysis',
		'expValue',
		'legacy',
		options,
	);
	return (
		experimentValue === 'fed' ||
		experimentValue === 'fed-optimized' ||
		fg('ai_3p_slack_federated_search_enabled')
	);
};
